import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { CollapsibleSection } from '../../components/CollapsibleSection'
import { RelatedList } from '../../design-system/list-view/RelatedList'
import { ForwardLookingCell } from '../../design-system/list-view/cells/ForwardLookingCell'
import { TimelinePanel } from '../../components/timeline/TimelinePanel'
import { WidgetErrorBoundary } from '../insights/widgets/WidgetErrorBoundary'
import { getContact } from '../../api/contacts'
import { listLegalEntities } from '../../api/legalEntities'
import { listCases } from '../../api/cases'
import { ApiError } from '../../api/problem'
import { rsqlEquals } from '../../lib/rsql'
import type { ContactView } from '../../types/contact'
import { buildLegalEntityColumns, LEGAL_ENTITY_COLUMN_ORDER } from '../legal-entities/columns'
import { buildCaseColumns, CASE_COLUMN_ORDER } from '../cases/caseColumns'
import { ContactFormModal } from './ContactFormModal'

const PHONE_GAP =
  'No contact-scoped phone read API is published yet — module.notify holds `phone_number` rows but ' +
  'has no `notify.api` port exposing them by contact (see IB-93 CRM-completeness disclosure). Flagged, not silently dropped.'

/**
 * The first-ever Contact detail page (CRM completeness — IB-93). Reached from the Contacts list
 * row-click/`Open` action. Header (name, Edit) -> `CollapsibleSection` "Details" (email, title,
 * linked Account, and a disclosed phone-numbers gap) -> `RelatedList` Legal entities + `RelatedList`
 * Cases, both scoped to the contact's parent Account (`Contact` itself carries no direct
 * legal-entity/case FK — data-model §3.2 — so "associated" here means "under the same Account",
 * giving the same cross-entity context a consultant-firm user gets from the Account page) ->
 * `TimelinePanel` for the Contact's own event stream.
 */
export function ContactDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [contact, setContact] = useState<ContactView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)

  const legalEntityColumns = useMemo(() => buildLegalEntityColumns(), [])
  const caseColumns = useMemo(() => buildCaseColumns(), [])

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoading(true)
    setError(null)
    getContact(id)
      .then(({ contact: c }) => {
        if (!cancelled) setContact(c)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.problem.detail ?? err.message : 'Could not load this contact.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
        <Link to="/clients/contacts" style={{ color: 'var(--iw-text-muted)' }}>
          Contacts
        </Link>{' '}
        › {contact?.name ?? id}
      </div>

      {error ? (
        <InlineAlert severity="error" action={<button type="button" className="iw-btn iw-btn--sm" onClick={() => navigate(0)}>Try again</button>}>
          {error}
        </InlineAlert>
      ) : loading ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>Loading contact…</p>
      ) : contact ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--iw-accent)' }}>
              <Icon name="users" size={22} />
            </span>
            <h1 style={{ margin: 0, fontSize: 'var(--iw-size-xl)' }}>{contact.name}</h1>
            <span style={{ flex: 1 }} />
            <button type="button" className="iw-btn" onClick={() => setEditing(true)}>
              Edit
            </button>
          </div>

          <CollapsibleSection title="Details">
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 'var(--iw-space-4)',
                margin: 0,
              }}
            >
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Email</dt>
                <dd style={{ margin: 0 }}>{contact.email}</dd>
              </div>
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Title</dt>
                <dd style={{ margin: 0 }}>{contact.title ?? <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>}</dd>
              </div>
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Account</dt>
                <dd style={{ margin: 0 }}>
                  <Link to={`/clients/accounts/${contact.accountId}`} style={{ color: 'var(--iw-accent)' }}>
                    {contact.accountName ?? contact.accountId}
                  </Link>
                </dd>
              </div>
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Phone</dt>
                <dd style={{ margin: 0 }}>
                  <ForwardLookingCell reason={PHONE_GAP} />
                </dd>
              </div>
            </dl>
          </CollapsibleSection>

          <WidgetErrorBoundary title="Legal entities" icon="landmark">
            <RelatedList
              storageKeyPrefix={`iw.contact-detail.${contact.id}.legal-entities`}
              entityIcon="landmark"
              title="Legal entities"
              columns={legalEntityColumns}
              defaultColumnOrder={LEGAL_ENTITY_COLUMN_ORDER}
              defaultSort={[{ columnId: 'name', direction: 'asc' }]}
              searchFields={['name', 'jurisdiction']}
              searchPlaceholder="Search legal entities…"
              parentFilter={rsqlEquals('accountId', contact.accountId)}
              fetchPage={({ filter, sort, cursor, limit }) => listLegalEntities({ filter, sort, cursor, limit })}
              getRowId={(row) => row.id}
              emptyTitle="No legal entities yet"
              emptyDescription={`No legal entities exist yet under ${contact.accountName ?? 'this account'}.`}
            />
          </WidgetErrorBoundary>

          <WidgetErrorBoundary title="Cases" icon="briefcase">
            <RelatedList
              storageKeyPrefix={`iw.contact-detail.${contact.id}.cases`}
              entityIcon="briefcase"
              title="Cases"
              columns={caseColumns}
              defaultColumnOrder={CASE_COLUMN_ORDER}
              defaultSort={[{ columnId: 'overallStatus', direction: 'asc' }]}
              parentFilter={rsqlEquals('accountId', contact.accountId)}
              fetchPage={({ filter, sort, cursor, limit }) => listCases({ filter, sort, cursor, limit })}
              getRowId={(row) => row.id}
              onRowActivate={(row) => navigate(`/cases/${row.id}`)}
              emptyTitle="No cases yet"
              emptyDescription={`No cases exist yet under ${contact.accountName ?? 'this account'}.`}
            />
          </WidgetErrorBoundary>

          <section>
            <h2 style={{ fontSize: 'var(--iw-size-md)', margin: '0 0 var(--iw-space-3)' }}>Timeline</h2>
            <TimelinePanel target={{ type: 'Contact', id: contact.id }} />
          </section>
        </>
      ) : null}

      {editing && contact ? (
        <ContactFormModal
          mode="edit"
          editing={{ contact, version: contact.version }}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            setContact(updated)
            setEditing(false)
          }}
        />
      ) : null}
    </div>
  )
}
